import {
  computed,
  EnvironmentProviders,
  inject,
  InjectionToken,
  provideEnvironmentInitializer,
  Provider,
  signal,
} from '@angular/core';
import {
  provideSearchModule,
  SOFTLINE_CONFIG_SEARCH_SHOW_NO_PERMISSION,
} from '@softapps/core';
import {
  Application,
  ApplicationStore,
  LinkModule,
  MenuItemService,
  SOFTLINE_FEATURE_APPLICATIONS,
} from '@softline/application';
import { authorizationFn } from '@softline/auth';
import { isDefinedNotEmpty, Store } from '@softline/core';
import {
  SOFTLINE_FEATURE_TRANSLATION,
  TranslationStore,
  translator,
} from '@softline/ui-core';
import { map, Observable, take } from 'rxjs';
import { default as de } from '../i18n/de.json';
import { default as en } from '../i18n/en.json';
import { AppSearchResult } from './search/app-search-result';
import { PinnedModulesService } from './services/pinned-modules.service';

export const SOFTLINE_CONFIG_MODULES_STATIC_LINKS = new InjectionToken<LinkModule[]>(
  'SOFTLINE_CONFIG_MODULES_STATIC_LINKS'
);

export interface ModuleOverviewConfig {
  preserveProviderOrder?: boolean;
}

export const SOFTLINE_CONFIG_MODULES = new InjectionToken<ModuleOverviewConfig>(
  'SOFTLINE_CONFIG_MODULES'
);

export { SOFTLINE_CONFIG_SEARCH_SHOW_NO_PERMISSION };

export function provideSoftAppsLinkModule(module: Omit<LinkModule, 'type'>) {
  return {
    provide: SOFTLINE_CONFIG_MODULES_STATIC_LINKS,
    useValue: {
      ...module,
      type: 'link',
    },
    multi: true,
  };
}

export const moduleProviders: (Provider | EnvironmentProviders)[] = [
  provideEnvironmentInitializer(() => {
    const store = inject(Store);
    store.commit(SOFTLINE_FEATURE_TRANSLATION, TranslationStore.mutations.add, {
      module: 'app-overview',
      language: 'de',
      translations: de,
    });

    store.commit(SOFTLINE_FEATURE_TRANSLATION, TranslationStore.mutations.add, {
      module: 'app-overview',
      language: 'en',
      translations: en,
    });

    const menuItemService = inject(MenuItemService);
    menuItemService.add(
      signal([
        {
          name: 'moduleRoute',
          type: 'route',
          outlet: 'nav-static',
          priority: 2,
          routerLink: ['/'],
          icon: 'fa-regular fa-grid',
          title: 'Alle Module',
        },
      ])
    );

    const isAuthorized = authorizationFn();
    const pinnedService = inject(PinnedModulesService);
    menuItemService.add(
      computed(() => {
        const routes = pinnedService.pinnedRoutes();
        const apps = store.get(
          SOFTLINE_FEATURE_APPLICATIONS,
          ApplicationStore.getters.items
        );
        return routes
          .map((route) => {
            const app = apps.find((a) => a.type !== 'link' && a.route === route);
            if (!app) return null;
            if (app.permission && !isAuthorized(app.permission)()) return null;
            return {
              name: 'pinned-' + route,
              type: 'route' as const,
              outlet: 'nav-pinned',
              routerLink: [route],
              icon: app.icon ?? 'fa-regular fa-thumbtack',
              title: app.name,
              tooltip: true,
            };
          })
          .filter((item): item is NonNullable<typeof item> => item !== null);
      })
    );
  }),
  provideSearchModule<Application>({
    url: '/',
    category: 'Module',
    icon: 'fa-regular fa-grid',
    component: AppSearchResult,
    factory: () => {
      const t = translator();
      const store = inject(Store);
      const showNoPermission = inject(SOFTLINE_CONFIG_SEARCH_SHOW_NO_PERMISSION);
      const staticLinks = inject(SOFTLINE_CONFIG_MODULES_STATIC_LINKS, { optional: true }) as LinkModule[] ?? [];
      const isAuthorizedSearch = authorizationFn();
      const applications$: Observable<Application[]> = store.observe(
        SOFTLINE_FEATURE_APPLICATIONS,
        ApplicationStore.getters.items
      );

      return {
        results(term: string | null): Observable<Application[]> {
          return applications$.pipe(
            take(1),
            map((applications) => {
              const normalizedTerm = term?.toLowerCase().trim();
              const settingsApp: Application = {
                name: '#UTILITIES.SETTINGS.TITLE',
                icon: 'fa-regular fa-gear',
                route: '/settings',
              };
              const allApps = [...applications, ...staticLinks, settingsApp];
              return allApps
                .filter((app) => {
                  const requiredPermission = app.permission?.trim();
                  if (requiredPermission && !isAuthorizedSearch(requiredPermission)())
                    return false;
                  if (!showNoPermission && !requiredPermission && ('route' in app ? app.route : '') !== '/settings') return false;
                  if (!normalizedTerm) return true;
                  return (
                    t(app.name)().toLowerCase().includes(normalizedTerm) ||
                    app.description?.toLowerCase().includes(normalizedTerm)
                  );
                })
                .sort((a, b) =>
                  t(a.name)().localeCompare(t(b.name)(), 'de', { sensitivity: 'base' })
                );
            })
          );
        },
      };
    },
  }),
];

export function provideModuleOverview(config?: ModuleOverviewConfig) {
  const providers = [...moduleProviders];

  if (isDefinedNotEmpty(config))
    providers.push({
      provide: SOFTLINE_CONFIG_MODULES,
      useValue: config,
    });

  return providers;
}
